import { useEffect, useRef, useState } from "react";
import { f5Speech } from "./lib/speech";

export function PermissionSetup({ onLocation }: { onLocation?: (position: GeolocationPosition) => void }) {
  const [missing, setMissing] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const attempt = useRef(0);
  const dismissed = useRef(false);
  function close() {
    dismissed.current = true;
    attempt.current++;
    setMissing(false);
    setBusy(false);
  }
  useEffect(() => {
    let alive = true;
    // Android 0.1.2+ already requests OS permissions at startup. WebView's
    // Permissions API can still report "prompt" after the OS grant; opening
    // another mandatory capture check here creates a duplicate permission loop.
    const nativeVersion = navigator.userAgent.match(/DestyAndroid\/(\d+)\.(\d+)\.(\d+)/);
    if (nativeVersion && (Number(nativeVersion[1]) > 0 || Number(nativeVersion[2]) > 1 || Number(nativeVersion[2]) === 1 && Number(nativeVersion[3]) >= 2)) return;
    Promise.all(["geolocation", "microphone"].map(async name => {
      try { return (await navigator.permissions.query({ name: name as PermissionName })).state; }
      catch { return "prompt"; }
    })).then(states => { if (alive && !dismissed.current) setMissing(states.some(state => state !== "granted")); });
    return () => { alive = false; attempt.current++; };
  }, []);
  useEffect(() => { if (missing) dialog.current?.showModal(); }, [missing]);
  async function enable() {
    const id = ++attempt.current;
    setBusy(true); setMessage("בודקים הרשאות מיקום ומיקרופון…");
    f5Speech.unlock();
    // Sequential requests prevent native permission dialogs from competing.
    const errors: string[] = [];
    try {
      await new Promise<void>((resolve, reject) => {
        const geo = window.DerechLocation ?? navigator.geolocation;
        if (!geo) { reject(new Error()); return; }
        geo.getCurrentPosition(position => {
          if (attempt.current === id && position?.coords && position.coords.accuracy <= 100) onLocation?.(position);
          resolve();
        }, error => error.code === 1 ? reject(error) : resolve(), {enableHighAccuracy: true, timeout: 10000, maximumAge: 5000});
      });
    } catch { errors.push("מיקום: יש לאפשר הרשאה בהגדרות האפליקציה"); }
    if (attempt.current !== id) return;
    try {
      await new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new DOMException("Microphone did not respond", "TimeoutError")), 12000);
        Promise.resolve().then(() => navigator.mediaDevices.getUserMedia({audio:true,video:false})).then(stream => {
          // Also release a late grant after dismissal or timeout.
          stream.getTracks().forEach(track => track.stop());
          resolve();
        }, reject).finally(() => window.clearTimeout(timer));
      });
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      errors.push(name === "NotFoundError" ? "לא נמצא מיקרופון זמין במכשיר" : name === "NotReadableError" ? "המיקרופון בשימוש או אינו זמין כרגע" : name === "TimeoutError" ? "בקשת המיקרופון לא הושלמה. אפשר להמשיך ולנסות שוב מחיפוש קולי" : "לא התקבלה גישה למיקרופון. אפשר להמשיך למפה ולבדוק את הרשאת המיקרופון בהגדרות המכשיר");
    }
    if (attempt.current !== id) return;
    setBusy(false);
    if (!errors.length) setMissing(false);
    else setMessage(errors.join(". "));
  }
  if (!missing) return null;
  return <dialog ref={dialog} className="modal permission-setup" aria-label="הרשאות ניווט וקול" onCancel={close}>
    <div className="modal-inner">
      <h2>מכינים את Desty לדרך</h2>
      <p>מיקום נדרש לניווט ולדיווחים. המיקרופון משמש לחיפוש ולפקודות קוליות כשמפעילים האזנה.</p>
      <p>להשמעה אין הרשאת מערכת נפרדת. אפשר לבדוק את הרמקול בחלונית הקול.</p>
      {message && <p role="status">{message}</p>}
      <button className="primary-btn" disabled={busy} onClick={() => void enable()}>אישור הרשאות מיקום ומיקרופון</button>
      <button className="text-btn" onClick={close}>המשך למפה</button>
    </div>
  </dialog>;
}
